import { Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon as CaretDownIconSmall } from '@persona-id/design-tokens/icons';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import { isEqual, pullAt } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import TruncatedText from 'dashboard/components/TruncatedText';
import HelpIcon from 'dashboard/components-lib/HelpIcon/HelpIcon';
import { CheckStatus } from 'dashboard/models/check';
import { CheckFilterValues } from 'dashboard/redux/actions/inquiries';
import Button from 'design-system/components/Button';
import Popover from 'design-system/components/Popover';
import { Label } from 'design-system/components/Text';
import inputMixin, { InputMixinProps, InputSizes } from 'design-system/mixins/input';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import useIsMounted from 'lib/hooks/useIsMounted';
import useToggle from 'lib/hooks/useToggle';
import { CheckName, CheckType } from 'lib/verificationCheck';

const Container = styled('div')`
  position: relative;
`;

const StyledButton = styled(Button)`
  padding-left: ${Spacing[5]};
`;

const StyledReactSelect = styled(ReactSelect<CheckType>)`
  min-width: 250px;
  padding-left: ${Spacing[4]};
`;

const Placeholder = styled('div')<InputMixinProps>`
  ${inputMixin};

  min-width: 200px;

  /* Fixes weird issue with shadow appearing inside placeholder text */
  user-select: none;

  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledIconCaretDownSmall = styled(CaretDownIconSmall)`
  pointer-events: none;
`;

const Option = styled('div')`
  display: flex;
  flex-direction: row;
  min-height: 70px;
  align-items: center;
  justify-content: start;
`;

const OptionLabelContainer = styled('span')`
  width: 70px;
  text-align: right;
`;

const RemoveCheck = styled('div')`
  padding: 0 ${Spacing[6]} 0 ${Spacing[6]};
  cursor: pointer;
`;

const getFilterLabel = (checkName: CheckName) => {
  return (
    <>
      <span>{CHECK_DISPLAY_CONFIG[checkName].name}</span>
      <HelpIcon>{CHECK_DISPLAY_CONFIG[checkName].description}</HelpIcon>
    </>
  );
};

const getCheckOptions = (checkType: CheckType): OptionType<CheckName>[] => {
  const filteredChecks = Object.keys(CHECK_DISPLAY_CONFIG).filter(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (checkName: CheckName) => CHECK_DISPLAY_CONFIG[checkName].type === checkType,
  );
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return Object.values(filteredChecks).map((checkName: CheckName) => ({
    label: getFilterLabel(checkName),
    value: checkName,
  }));
};

const verificationValueOptions: OptionType<CheckType>[] = [
  {
    label: 'Database AAMVA',
    value: CheckType.Aamva,
  },
  {
    label: 'Business Website',
    value: CheckType.BusinessWebsite,
  },
  {
    label: 'Business Registry',
    value: CheckType.DatabaseBusiness,
  },
  {
    label: 'Business Footprint',
    value: CheckType.DatabaseBusinessFootprint,
  },
  {
    label: 'Database',
    value: CheckType.Database,
  },
  {
    label: 'Database eCBSV',
    value: CheckType.DatabaseEcbsv,
  },
  {
    label: 'Database Phone Carrier',
    value: CheckType.DatabasePhoneCarrier,
  },
  {
    label: 'Database Serpro',
    value: CheckType.DatabaseSerpro,
  },
  {
    label: 'Database TIN',
    value: CheckType.DatabaseTin,
  },
  {
    label: 'Database VAT Number',
    value: CheckType.DatabaseVatNumber,
  },
  {
    label: 'Document',
    value: CheckType.Document,
  },
  {
    label: 'Email Address',
    value: CheckType.EmailAddress,
  },
  {
    label: 'Government ID',
    value: CheckType.Id,
  },
  {
    label: 'Government ID NFC',
    value: CheckType.IdNfc,
  },
  {
    label: 'Mobile Drivers Licence',
    value: CheckType.MobileDriversLicense,
  },
  {
    label: 'Phone Number',
    value: CheckType.PhoneNumber,
  },
  {
    label: 'Selfie',
    value: CheckType.Selfie,
  },
];

interface CheckRowValue extends CheckFilterValues {
  verification: CheckType;
}

interface CheckRowProps {
  idx: number;
  initialCheckValue: CheckName;
  initialVerificationValue: CheckType;
  initialStatusValue: CheckStatus;
  onSubmit: (idx: number, row: CheckRowValue) => void;
  onRemoveRow: (idx: number) => void;
  verificationOptions: OptionType<CheckType>[];
}

const CheckRow = ({
  idx,
  initialCheckValue,
  initialVerificationValue,
  initialStatusValue,
  onSubmit,
  onRemoveRow,
  verificationOptions,
}: CheckRowProps) => {
  const [checkValueOptions, setCheckValueOptions] = useState<OptionType<CheckName>[] | null>(null);
  const [verificationValue, setVerificationValue] = useState<CheckType | null>(
    initialVerificationValue,
  );
  const [checkValue, setCheckValue] = useState<CheckName | null>(initialCheckValue);
  const [statusValue, setStatusValue] = useState<CheckStatus | null>(initialStatusValue);

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setCheckValueOptions(getCheckOptions(verificationValue));
  }, [verificationValue]);

  const statusValueOptions: OptionType<CheckStatus>[] = [
    {
      label: 'Passed',
      value: CheckStatus.Passed,
      color: 'green',
    },
    {
      label: 'Failed',
      value: CheckStatus.Failed,
      color: 'red',
    },
  ];

  statusValueOptions.push({
    label: 'Not Applicable',
    value: CheckStatus.NotApplicable,
    color: 'slate',
  });

  const submitRowIfValid = (newRow: CheckRowValue) => {
    // Since changing the verification type resets the check options,
    // we only care about submitting row value changes if the check or status value changed
    if (filterRowIsValid(newRow)) {
      onSubmit(idx, newRow);
    }
  };

  const handleRemoveRow = useCallback(() => {
    // onRemoveRow doesn't remove row if only 1 row is present; reset state if so
    // We do this first to avoid updating state after unmount.
    setCheckValueOptions(null);
    setVerificationValue(null);
    setCheckValue(null);
    setStatusValue(null);
    onRemoveRow(idx);
  }, [onRemoveRow, idx]);

  return (
    <Option>
      <OptionLabelContainer>
        <Label size='lg' weight='bold'>
          {idx === 0 ? 'Where' : 'And'}
        </Label>
      </OptionLabelContainer>
      <StyledReactSelect
        id='inquiry-filters__verification'
        onChange={setVerificationValue}
        options={verificationOptions}
        isSearchable={true}
        placeholder='Select verification'
        value={verificationValue}
      />
      <StyledReactSelect
        id='inquiry-filters__check'
        onChange={(value: CheckName) => {
          setCheckValue(value);
          // We do this because the state value is not necessarily updated in time for submitting
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          submitRowIfValid({ verification: verificationValue, check: value, status: statusValue });
        }}
        options={checkValueOptions}
        isSearchable={true}
        placeholder='Select check'
        isDisabled={verificationValue == null}
        value={checkValue}
      />
      <StyledReactSelect
        id='inquiry-filters__check-status'
        onChange={(value: CheckStatus) => {
          setStatusValue(value);
          // We do this because the state value is not necessarily updated in time for submitting
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          submitRowIfValid({ verification: verificationValue, check: checkValue, status: value });
        }}
        options={statusValueOptions}
        isSearchable={true}
        placeholder='Select status'
        value={statusValue}
      />
      <RemoveCheck
        onClick={handleRemoveRow}
        id='row__removeIcon-check'
        data-test='row__removeIcon-check'
      >
        <XIcon />
      </RemoveCheck>
    </Option>
  );
};

const EMPTY_FILTER_ROW: CheckRowValue = {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  verification: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  check: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  status: null,
};

const filterRowIsValid = (row: CheckRowValue) =>
  row.verification != null && row.check != null && row.status != null;

export interface ChecksFilterProps {
  onChange: (values: CheckFilterValues[]) => void;
  value: CheckFilterValues[];
  disabled?: boolean;
  dataTest?: string;
  size?: InputSizes;
  className?: string;
}

const ChecksFilter = ({
  onChange,
  value,
  disabled = false,
  dataTest,
  size = 'md',
  className,
}: ChecksFilterProps) => {
  const [showOptionsDropdown, { set: setShowOptionsDropdown }] = useToggle(false);

  const valueWithCheckTypes = useMemo<CheckRowValue[]>(
    () =>
      value?.map((checkFilterValue) => ({
        ...checkFilterValue,
        verification: CHECK_DISPLAY_CONFIG[checkFilterValue.check].type,
      })),
    [value],
  );

  const [checksFilterRows, setChecksFilterRows] = useState<CheckRowValue[]>(
    valueWithCheckTypes ?? [EMPTY_FILTER_ROW],
  );

  const handleAddChecksFilterRow = () => {
    setChecksFilterRows(checksFilterRows.concat(EMPTY_FILTER_ROW));
  };

  const handleSubmitWithNewRow = (rowIndex: number, row: CheckRowValue) => {
    const updatedRowValues = [...checksFilterRows];
    updatedRowValues[rowIndex] = row;
    setChecksFilterRows(updatedRowValues);
  };

  const removeOrClearRow = (idx: number) => {
    if (idx === 0 && checksFilterRows.length === 1) {
      setChecksFilterRows([EMPTY_FILTER_ROW]);
    } else {
      const updatedRowValues = pullAt([idx], checksFilterRows);
      setChecksFilterRows(updatedRowValues);
    }
  };

  const getSummaryLabelText = () => {
    const filledRows = checksFilterRows.filter((row) => !isEqual(row, EMPTY_FILTER_ROW));
    const numRows = filledRows.length;
    if (numRows === 0) {
      return 'All verification checks';
    } else if (numRows === 1) {
      return '1 Check';
    } else {
      return numRows + ' Checks';
    }
  };

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const filteredRows: CheckFilterValues[] = useMemo(() => {
    const filteredRows = checksFilterRows
      .filter((row) => !isEqual(row, EMPTY_FILTER_ROW))
      // Strip verification (check type) when persisting as we don't need it
      .map(({ verification, ...row }) => row);
    return filteredRows.length === 0 ? undefined : filteredRows;
  }, [checksFilterRows]);

  const isMounted = useIsMounted();
  useEffect(() => {
    if (!isMounted()) {
      return;
    }

    onChange(filteredRows);
    // Use JSON.stringify to only change on deep equality change.
    // We need this because we have two useEffects that would cause an infinite
    // loop if we only rely on shallow equality checks.
  }, [JSON.stringify(filteredRows)]);

  useEffect(() => {
    if (valueWithCheckTypes) {
      setChecksFilterRows(valueWithCheckTypes);
    }
  }, [valueWithCheckTypes]);

  return (
    <Container data-test={dataTest} className={className}>
      <Popover
        trigger={
          <Placeholder id='checksFilter__placeholder' $size={size} disabled={disabled}>
            <TruncatedText>{getSummaryLabelText()}</TruncatedText>
            <StyledIconCaretDownSmall />
          </Placeholder>
        }
        open={showOptionsDropdown}
        onOpenChange={(open) => setShowOptionsDropdown(open)}
        contentProps={{
          side: 'bottom',
          align: 'start',
          collisionBoundary: [document.getElementById('main')],
        }}
      >
        {checksFilterRows.map((val, idx) => (
          <CheckRow
            key={`check-row-${idx}-${JSON.stringify(val)}`}
            idx={idx}
            initialCheckValue={val['check']}
            initialVerificationValue={val['verification']}
            initialStatusValue={val['status']}
            onSubmit={handleSubmitWithNewRow}
            onRemoveRow={removeOrClearRow}
            verificationOptions={verificationValueOptions}
          />
        ))}
        <StyledButton
          id='checks-filter__submit'
          onClick={handleAddChecksFilterRow}
          disabled={!checksFilterRows.every(filterRowIsValid)}
          variant='basic'
          width='auto'
        >
          Add Filter
        </StyledButton>
      </Popover>
    </Container>
  );
};

/**
 * Special styling for use with new table filters UI
 */
export const SegmentedChecksFilter = styled(ChecksFilter)`
  ${Placeholder} {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-left: -1px;
  }
`;

export default ChecksFilter;
